/**
 * Layout Selector Component
 * Visual selector for multi-chart layouts
 */

import React, { useState } from 'react';
import { LayoutType, LAYOUT_TEMPLATES } from '../types/chartLayout';

interface LayoutSelectorProps {
  currentLayout: LayoutType;
  onLayoutChange: (layout: LayoutType) => void;
  syncCrosshair?: boolean;
  syncTimeframe?: boolean;
  syncSymbol?: boolean;
  onToggleSync?: (type: 'crosshair' | 'timeframe' | 'symbol') => void;
  disabled?: boolean;
}

export const LayoutSelector: React.FC<LayoutSelectorProps> = ({ 
  currentLayout, 
  onLayoutChange,
  syncCrosshair = false,
  syncTimeframe = false,
  syncSymbol = false,
  onToggleSync,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggleSync = (type: 'crosshair' | 'timeframe' | 'symbol') => {
    if (onToggleSync) {
      onToggleSync(type);
    }
  };

  const renderLayoutIcon = (type: LayoutType) => {
    const template = LAYOUT_TEMPLATES.find(t => t.type === type);
    if (!template) return null;

    const { rows, cols } = template;
    const cellWidth = 100 / cols;
    const cellHeight = 100 / rows;

    return (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        {Array.from({ length: rows }).map((_, rowIdx) =>
          Array.from({ length: cols }).map((_, colIdx) => (
            <rect
              key={`${rowIdx}-${colIdx}`}
              x={colIdx * cellWidth + 2}
              y={rowIdx * cellHeight + 2}
              width={cellWidth - 4}
              height={cellHeight - 4}
              fill="currentColor"
              className="opacity-80"
              rx="2"
            />
          ))
        )}
      </svg>
    );
  };

  return (
    <div className="relative">
      {/* Current Layout Button */}
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`flex items-center gap-2 px-3 py-1.5 border rounded-lg transition-colors text-xs font-medium ${
          disabled 
            ? 'bg-slate-800/50 border-slate-800 text-slate-600 cursor-not-allowed opacity-60' 
            : 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-300 hover:text-white'
        }`}
        title={disabled ? 'Layout changes disabled during replay mode' : 'Change chart layout'}
      >
        <div className="w-4 h-4 text-emerald-400">
          {renderLayoutIcon(currentLayout)}
        </div>
        <span>Layout</span>
        <svg 
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Layout Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Dropdown Menu */}
          <div className="absolute top-full right-0 mt-2 w-72 max-h-[calc(100vh-100px)] overflow-y-auto bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-50 p-3 scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800">
            <div className="text-xs font-semibold text-slate-400 mb-3 px-1">
              Select Chart Layout
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              {LAYOUT_TEMPLATES.map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    onLayoutChange(template.type);
                    setIsOpen(false);
                  }}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg transition-all ${
                    currentLayout === template.type
                      ? 'bg-emerald-500/20 border-2 border-emerald-500 text-emerald-400'
                      : 'bg-slate-800 hover:bg-slate-700 border-2 border-transparent text-slate-400 hover:text-white'
                  }`}
                  title={template.description}
                >
                  <div className="w-8 h-8">
                    {renderLayoutIcon(template.type)}
                  </div>
                  <span className="text-[10px] font-medium text-center leading-tight">
                    {template.name}
                  </span>
                </button>
              ))}
            </div>

            {/* Sync Options */}
            <div className="mt-3 pt-3 border-t border-slate-800">
              <div className="text-xs font-semibold text-slate-400 mb-2 px-1">
                Sync Options
              </div>
              
              <div className="space-y-2">
                {/* Crosshair Sync */}
                <div className="flex items-center justify-between px-2 py-1.5 bg-slate-800/50 rounded">
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-xs text-slate-300">Crosshair</span>
                  </div>
                  <button
                    onClick={() => handleToggleSync('crosshair')}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      syncCrosshair ? 'bg-emerald-500' : 'bg-slate-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        syncCrosshair ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Timeframe Sync */}
                <div className="flex items-center justify-between px-2 py-1.5 bg-slate-800/50 rounded">
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-slate-300">Time</span>
                  </div>
                  <button
                    onClick={() => handleToggleSync('timeframe')}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      syncTimeframe ? 'bg-emerald-500' : 'bg-slate-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        syncTimeframe ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Symbol Sync */}
                <div className="flex items-center justify-between px-2 py-1.5 bg-slate-800/50 rounded">
                  <div className="flex items-center gap-2">
                    <svg className="w-3.5 h-3.5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                    </svg>
                    <span className="text-xs text-slate-300">Symbol</span>
                  </div>
                  <button
                    onClick={() => handleToggleSync('symbol')}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      syncSymbol ? 'bg-emerald-500' : 'bg-slate-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                        syncSymbol ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
